import $script from 'scriptjs';
import axios from 'axios';

function getAddressComponent(autocompletedData, componentPiece, part = 'long_name') {
  if (autocompletedData.address_components) {
    return autocompletedData.address_components.reduce((piece, component) => {
      if (component.types.indexOf(componentPiece) !== -1) {
        piece = component[part];
      }
      return piece;
    }, null);
  }
  return null;
}

function extractAddressData(place) {
  const street_number = getAddressComponent(place, 'street_number');
  const route = getAddressComponent(place, 'route');

  return {
    formatted_address: place.formatted_address || '',
    address1: `${street_number ? `${street_number} ` : ''}${route}`,
    city: getAddressComponent(place, 'locality') || '',
    state: getAddressComponent(place, 'administrative_area_level_1', 'short_name') || '',
    zip: getAddressComponent(place, 'postal_code') || '',
    country: getAddressComponent(place, 'country', 'short_name') || '',
    lat: place.geometry.location.lat().toString(),
    lng: place.geometry.location.lng().toString(),
    loadTimezone: true,
  };
}

/**
 * Used to bias the autocomplete object to the user's geographical location,
 * as supplied by the browser's 'navigator.geolocation' object.
 */
function geolocate() {
  if (navigator.geolocation && location.protocol === 'https:') {
    navigator.geolocation.getCurrentPosition(position => {
      const circle = new google.maps.Circle({
        center: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
        radius: position.coords.accuracy,
      });
      this.setBounds(circle.getBounds());
    });
  }
}

let sessionToken = null;

/**
 * Wrapper for Google Places Autocomplete javascript API
 * Reference: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete
 * Example: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
 *
 * @class GooglePlaces
 */
class GooglePlaces {
  shouldLog = () => window.app.env !== 'production';

  googlePlacesIsLoaded = new Promise((resolve, reject) => {
    window.googlePlacesIsLoaded = () => {
      sessionToken = new google.maps.places.AutocompleteSessionToken();
      resolve();
    };
    $script(
      `https://maps.googleapis.com/maps/api/js?key=${window.app.googleMapsApiKey}&language=en&libraries=places&callback=googlePlacesIsLoaded&loading=async`,
    );
  });

  async createAutocomplete(element, placeChangedCallback) {
    await this.googlePlacesIsLoaded;

    let autocomplete = new google.maps.places.Autocomplete(element, { types: ['geocode'] });

    autocomplete.addListener('place_changed', () => placeChangedCallback(autocomplete.getPlace()));
    // autocomplete.geolocate = geolocate.bind(autocomplete); // disabled geolocate so it doesnt ask for permissions
    autocomplete.dispose = () => google.maps.event.clearInstanceListeners(autocomplete);

    return autocomplete;
  }

  /**
   * Get address suggestions from google for a certain input
   */
  async getSuggestions(input) {
    await this.googlePlacesIsLoaded;
    return new Promise((resolve, reject) => {
      new google.maps.places.AutocompleteService().getPlacePredictions(
        { input, sessionToken },
        (predictions, status) => {
          this.shouldLog() && console.info('Google place predictions', predictions);
          if (status != google.maps.places.PlacesServiceStatus.OK) {
            reject(status);
          } else {
            resolve(predictions);
          }
        },
      );
    });
  }

  async placeAddress(placeId) {
    await this.googlePlacesIsLoaded;
    return new Promise((resolve, reject) => {
      new google.maps.places.PlacesService(document.createElement('div')).getDetails(
        {
          sessionToken,
          placeId,
          fields: ['formatted_address', 'address_components', 'geometry'],
        },
        (place, status) => {
          this.shouldLog() && console.info('Google place', place);
          if (status != google.maps.places.PlacesServiceStatus.OK) {
            reject(status);
          } else {
            const address = extractAddressData(place);
            this.shouldLog() && console.info('Resolved address', address);
            resolve(address);
          }
        },
      );
    });
  }

  async locationTimezone(lat, lng) {
    var date = new Date();

    const response = await axios.get(`https://maps.googleapis.com/maps/api/timezone/json`, {
      params: {
        location: `${lat},${lng}`,
        timestamp: date.getTime() / 1000 + date.getTimezoneOffset() * 60,
        key: window.app.googleTimezoneApiKey,
        loading: 'async'
      },
    });

    this.shouldLog() && console.info('Google location timezone', { lat, lng }, response.data);
    return response.data.timeZoneId;
  }
}

export default new GooglePlaces();
